import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SuperAdminNodesViewer from './SuperAdminNodesViewer';
import SuperAdminPromptInjectionLogs from './SuperAdminPromptInjectionLogs';
import EntityRequestManager from './EntityRequestManager';
import SuperAdminTenantSelector from './SuperAdminTenantSelector';
import FeatureManager from './FeatureManager';
import LicenseAdminDashboard from './LicenseAdminDashboard';
import FinancialAdminDashboard from './FinancialAdminDashboard';
import UsageDashboard from './UsageDashboard';
import ImpactAnalyzer from './ui/ImpactAnalyzer';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import '../styles/super-admin-enhanced.css';

const RoleManager = ({ userId, currentRole, onRoleChange }) => {
  const { t } = useTranslation();
  const [newRole, setNewRole] = useState(currentRole);
  const [loading, setLoading] = useState(false);

  const handleRoleUpdate = async () => {
    setLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/super-admin/users/${userId}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      onRoleChange(newRole);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du rôle:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="role-manager">
      <select value={newRole} onChange={(e) => setNewRole(e.target.value)} disabled={loading}>
        <option value="user">Utilisateur</option>
        <option value="admin">Administrateur</option>
        <option value="super-admin">Super Administrateur</option>
      </select>
      <button onClick={handleRoleUpdate} disabled={loading || newRole === currentRole}>
        {loading ? t('superAdminDashboardEnhanced.updating') : t('superAdminDashboardEnhanced.update')}
      </button>
    </div>
  );
};

const SuperAdminDashboard = ({ user, onLogout, initialTab = 'overview' }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [notifications, setNotifications] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  // Nouvelles states pour fonctionnalités intégrées
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [originalTenant, setOriginalTenant] = useState(null);
  const [organizationForm, setOrganizationForm] = useState({
    name: '',
    description: '',
    website: '',
    plan: 'free',
    adminEmail: '',
    adminPassword: '',
    adminFirstName: '',
    adminLastName: ''
  });
  const [creating, setCreating] = useState(false);
  const [orphanUsers, setOrphanUsers] = useState([]);
  const [migrationTarget, setMigrationTarget] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [users, setUsers] = useState([]);

  // États pour la gestion des migrations
  const [selectedUsersForMigration, setSelectedUsersForMigration] = useState([]);
  const [migrationStrategy, setMigrationStrategy] = useState('merge');
  const [targetOrganizationForMigration, setTargetOrganizationForMigration] = useState('');
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [loadingMigrationPreview, setLoadingMigrationPreview] = useState(false);
  const [executingMigration, setExecutingMigration] = useState(false);
  const [resetUserId, setResetUserId] = useState('');
  const [resetOptions, setResetOptions] = useState({
    nodes: true,
    relations: true,
    tests: true,
    analytics: false
  });
  const [executingReset, setExecutingReset] = useState(false);

  // États pour la réinitialisation du mot de passe
  const [selectedUserForPwdReset, setSelectedUserForPwdReset] = useState('');
  const [passwordResetting, setPasswordResetting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordResetAlert, setPasswordResetAlert] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    // Sauvegarder le tenant d'origine du super admin
    setOriginalTenant({
      tenantId: user.tenantId,
      name: 'Super Admin Global'
    });
  }, [user]);

  useEffect(() => {
    loadSuperAdminData();
    fetchUsers();
    fetchOrganizations();
    loadOrphanUsers();
  }, []);

  const loadSuperAdminData = async () => {
    try {
      console.log('🔄 Chargement des vraies données depuis le backend...');

      // Fetch global stats avec withCredentials pour inclure les cookies httpOnly
      const statsResponse = await axios.get(`${API_BASE_URL}/api/super-admin/stats`, {
        withCredentials: true
      });
      // console.log('📊 Stats reçues:', statsResponse.data); // COMMENTÉ - contient données stats sensibles

      // Adapter les données pour le format attendu par le dashboard
      const backendStats = statsResponse.data;
      setStats({
        organizationsCount: backendStats.organizations?.total || 0,
        usersCount: backendStats.users?.total || 0,
        graphsCount: backendStats.graphs?.total || 0,
        todayLogins: backendStats.users?.activeToday || 0,
        avgResponseTime: 120, // Mock data - à remplacer par vraies métriques
        requestsPerHour: 1250,
        failedLogins: backendStats.security?.failedLogins || 0,
        activeTokens: backendStats.security?.activeTokens || 0,
        recentActivity: backendStats.activity || []
      });

      // Fetch organizations
      const orgsResponse = await axios.get(`${API_BASE_URL}/api/super-admin/tenants`, {
        withCredentials: true
      });
      // console.log('🏢 Organisations reçues:', orgsResponse.data); // COMMENTÉ - contient données organisations sensibles
      setOrganizations(orgsResponse.data.tenants || []);

      // Fetch users  
      const usersResponse = await axios.get(`${API_BASE_URL}/api/super-admin/users`, {
        withCredentials: true
      });
      // console.log('👥 Utilisateurs reçus:', usersResponse.data); // COMMENTÉ - contient données utilisateurs sensibles
      setAllUsers(usersResponse.data.users || []);

      // Load notifications from backend
      try {
        const notificationsResponse = await axios.get(`${API_BASE_URL}/api/super-admin/notifications?limit=25&offset=0`, {
          withCredentials: true
        });
        const items = (notificationsResponse.data.notifications || []).map(n => ({
          id: n._id || n.id,
          type: n.type || 'info',
          message: n.message || n.title || '',
          time: n.createdAt ? new Date(n.createdAt).toLocaleString() : ''
        }));
        setNotifications(items);
      } catch (notifError) {
        console.error('Erreur lors de la récupération des notifications:', notifError);
        // Fallback to empty list on error
        setNotifications([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données:', error);
      if (error.response?.status === 401) {
        alert(t('superAdminDashboardEnhanced.sessionExpired'));
        if (onLogout) onLogout();
      } else {
        console.error('Détails de l\'erreur:', error.response?.data || error.message);
        // En cas d'erreur, charger avec des valeurs par défaut
        setStats({
          organizationsCount: 0,
          usersCount: 0,
          graphsCount: 0,
          todayLogins: 0,
          avgResponseTime: 0,
          requestsPerHour: 0,
          failedLogins: 0,
          activeTokens: 0,
          recentActivity: []
        });
        setOrganizations([]);
        setAllUsers([]);
        setNotifications([]);
        setLoading(false);
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/api/super-admin/users?tenantId=super_admin_tenant');
      // console.log('Réponse API utilisateurs:', response.data); // COMMENTÉ - contient données utilisateurs sensibles
      if (Array.isArray(response.data.users)) {
        // Filtrer : pas de super-admin, pas d'organisation, pas d'admin d'organisation
        const orphanUsers = response.data.users.filter(user =>
          user.role !== 'super-admin' &&
          !user.organizationId &&
          user.role !== 'org-admin' &&
          user.organization === 'Aucune organisation'
        );
        setUsers(orphanUsers);
      } else {
        console.error('La réponse de l\'API n\'est pas un tableau:', response.data.users);
        setUsers([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      setUsers([]);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await apiClient.get('/api/super-admin/tenants');
      // console.log('Réponse API organisations:', response.data); // COMMENTÉ - contient données organisations sensibles
      if (Array.isArray(response.data.tenants)) {
        setOrganizations(response.data.tenants);
      } else {
        console.error('La réponse de l\'API n\'est pas un tableau:', response.data.tenants);
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des organisations:', error);
      setOrganizations([]);
    }
  };

  // Fonctions intégrées du SuperAdminInterface
  const handleTenantSelected = async (tenant) => {
    if (!tenant) {
      setSelectedTenant(null);
      setActiveTab('support-tenants');
      await restoreOriginalContext();
      return;
    }

    setSelectedTenant(tenant);
    await switchToTenantContext(tenant);
  };

  const switchToTenantContext = async (tenant) => {
    try {
      const response = await apiClient.post('/api/super-admin/impersonate', {
        tenantId: tenant.tenantId
      });

      // Stocker le token temporaire
      localStorage.setItem('tempSuperAdminToken', response.data.token);
      console.log('🔄 Contexte temporaire activé pour:', tenant.name);
    } catch (error) {
      console.error('❌ Erreur lors du changement de contexte:', error);
    }
  };

  const restoreOriginalContext = async () => {
    try {
      await apiClient.post('/api/super-admin/restore-context');
      localStorage.removeItem('tempSuperAdminToken');
      console.log('🏠 Contexte super admin restauré');
    } catch (error) {
      console.error('❌ Erreur lors de la restauration du contexte:', error);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await apiClient.post('/api/super-admin/create-organization', organizationForm);

      setOrganizationForm({
        name: '',
        description: '',
        website: '',
        plan: 'free',
        adminEmail: '',
        adminPassword: '',
        adminFirstName: '',
        adminLastName: ''
      });

      setActiveTab('organizations');
      // console.log('✅ Organisation créée:', response.data); // COMMENTÉ - contient données organisation sensibles

      // Recharger les données
      await fetchOrganizations();

    } catch (error) {
      console.error('❌ Erreur création organisation:', error);
    } finally {
      setCreating(false);
    }
  };

  // Fonctions manquantes pour la gestion des utilisateurs
  const handleGenerateInvitationCode = async (email, tenantId, role = 'user') => {
    try {
      const response = await apiClient.post('/api/super-admin/invitations', {
        email,
        tenantId,
        role,
        expiresInMinutes: 30
      });

      // console.log('✅ Code d\'invitation généré:', response.data.code); // COMMENTÉ - contient code invitation sensible
      alert(`Code d'invitation généré: ${response.data.code}`);
    } catch (error) {
      console.error('❌ Erreur lors de la génération du code d\'invitation:', error);
      alert(t('superAdminDashboardEnhanced.invitationCodeError'));
    }
  };

  const handleApplyInvitationCode = async (email, tenantId, role) => {
    try {
      const response = await apiClient.post('/api/super-admin/apply-invitation', {
        email,
        tenantId,
        role
      });

      // console.log('✅ Invitation appliquée avec succès:', response.data); // COMMENTÉ - contient données invitation sensibles
      alert(`Utilisateur ${email} associé à l'organisation avec succès.`);

      // Recharger les données
      await fetchUsers();
      await fetchOrganizations();
    } catch (error) {
      console.error('❌ Erreur lors de l\'application de l\'invitation:', error);
      alert('Erreur lors de l\'application de l\'invitation.');
    }
  };

  const handleRemoveUserFromOrganization = async (email) => {
    try {
      const response = await apiClient.post('/api/super-admin/remove-user-from-organization', {
        email
      });

      // console.log('✅ Utilisateur retiré avec succès:', response.data); // COMMENTÉ - contient données utilisateur sensibles
      alert(`Utilisateur ${email} retiré de l'organisation avec succès.`);

      // Mettre à jour la liste des utilisateurs
      const updatedUsers = users.map(user => {
        if (user.email === email) {
          return { ...user, organizationId: null, role: 'user' };
        }
        return user;
      });
      setUsers(updatedUsers);

      // Recharger les données
      await fetchUsers();
    } catch (error) {
      console.error('❌ Erreur lors du retrait de l\'utilisateur:', error);
      alert('Erreur lors du retrait de l\'utilisateur.');
    }
  };

  const handleMigrateUsers = async () => {
    if (!migrationTarget) {
      alert(t('superAdminDashboardEnhanced.selectTargetOrganization'));
      return;
    }

    setMigrating(true);
    try {
      const response = await apiClient.post('/api/super-admin/migrate-users', {
        targetTenantId: migrationTarget
      });

      alert(`✅ ${response.data.migratedCount} utilisateurs migrés avec succès`);

      // Recharger les données
      await loadOrphanUsers();
      await fetchOrganizations();

      setMigrationTarget('');
    } catch (error) {
      console.error('❌ Erreur migration:', error);
      alert('Erreur lors de la migration');
    } finally {
      setMigrating(false);
    }
  };

  const loadOrphanUsers = async () => {
    try {
      const response = await apiClient.get('/api/super-admin/orphan-users');
      setOrphanUsers(response.data.users || []);
    } catch (error) {
      console.error('Erreur chargement utilisateurs orphelins:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      // ignore
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('superAdminToken');
      localStorage.removeItem('authUser');
      localStorage.removeItem('superAdminUser');
      localStorage.removeItem('authData');
      localStorage.removeItem('authData_backup');
      if (typeof onLogout === 'function') {
        onLogout();
      }
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.href = '/';
        }, 100);
      }
    }
  };

  const tabs = [
    { id: 'overview', label: "Vue d'ensemble", icon: '📊' },
    { id: 'licenses', label: 'Licences', icon: '🎫' },
    { id: 'financial', label: 'Financier', icon: '💰' },
    { id: 'nodes', label: 'Nœuds Globaux', icon: '🌐' },
    { id: 'organizations', label: 'Organisations', icon: '🏢' },
    { id: 'users', label: 'Utilisateurs', icon: '👥' },
    { id: 'migrations', label: 'Migrations', icon: '🔄' },
    { id: 'requests', label: 'Demandes', icon: '🔔' },
    { id: 'support-tenants', label: 'Support Tenants', icon: '🛠️' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'system', label: 'Système', icon: '🔧' },
    { id: 'security', label: 'Sécurité', icon: '🛡️' }
  ];

  const handleViewUser = (user) => {
    alert(`Détails de l'utilisateur:\nNom: ${user.firstName} ${user.lastName}\nEmail: ${user.email}\nRôle: ${user.role}\nStatut: ${user.status}\nDernière connexion: ${user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Jamais'}`);
  };

  const handleEditUser = (user) => {
    setUserToEdit(user);
    setIsEditModalOpen(true);
  };

  const saveUserRole = () => {
    if (userToEdit) {
      const userEmail = userToEdit.email;
      alert(`Rôle mis à jour pour ${userToEdit.firstName} ${userToEdit.lastName}: ${userToEdit.role}`);
      setAllUsers((prevUsers) => prevUsers.map((u) =>
        u.email === userEmail ? userToEdit : u
      ));
      setIsEditModalOpen(false);
    }
  };

  const handleDeleteUser = (user) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${user.firstName} ${user.lastName} ?`)) {
      alert('Utilisateur supprimé !');
      // Simuler la suppression locale
      setAllUsers((prevUsers) => prevUsers.filter((u) => u.email !== user.email));
    }
  };

  const saveUserDetails = async () => {
    if (userToEdit) {
      try {
        const response = await apiClient.put(`/api/super-admin/users/${userToEdit._id}`, {
          firstName: userToEdit.firstName,
          lastName: userToEdit.lastName,
          email: userToEdit.email,
          role: userToEdit.role,
          status: userToEdit.status
        });

        alert(`Détails mis à jour pour ${userToEdit.firstName} ${userToEdit.lastName}`);
        setAllUsers((prevUsers) => prevUsers.map((u) => (u._id === userToEdit._id ? userToEdit : u)));
        setIsEditModalOpen(false);

        // Recharger les données pour être sûr
        await loadSuperAdminData();
      } catch (error) {
        console.error('Erreur lors de la mise à jour:', error);
        alert('Erreur lors de la mise à jour de l\'utilisateur');
      }
    }
  };

  // ===== FONCTIONS DE GESTION DES MIGRATIONS =====

  const handleGenerateMigrationPreview = async () => {
    if (selectedUsersForMigration.length === 0 || !targetOrganizationForMigration) {
      alert('Veuillez sélectionner des utilisateurs et une organisation cible');
      return;
    }

    setLoadingMigrationPreview(true);
    try {
      const response = await apiClient.post('/api/super-admin/preview-user-migration', {
        userIds: selectedUsersForMigration,
        targetOrganizationId: targetOrganizationForMigration,
        strategy: migrationStrategy
      });

      setMigrationPreview(response.data);
    } catch (error) {
      console.error('Erreur lors de la génération de l\'aperçu:', error);
      alert('Erreur lors de la génération de l\'aperçu de migration');
    } finally {
      setLoadingMigrationPreview(false);
    }
  };

  const handleExecuteMigration = async () => {
    if (!migrationPreview) {
      alert('Veuillez d\'abord générer un aperçu de migration');
      return;
    }

    const confirmation = window.confirm(
      `Êtes-vous sûr de vouloir migrer ${selectedUsersForMigration.length} utilisateur(s) vers l'organisation cible ?\n\n` +
      `Cette action est irréversible et créera automatiquement des backups.`
    );

    if (!confirmation) return;

    setExecutingMigration(true);
    try {
      const migrationPlans = migrationPreview.users.map(userData => ({
        userId: userData.userId,
        targetOrganizationId: targetOrganizationForMigration,
        strategy: migrationStrategy,
        migrationPlan: userData.migrationPlan
      }));

      const response = await apiClient.post('/api/super-admin/execute-user-migration', {
        migrationPlans
      });

      alert(`✅ Migration réussie!\n\n${response.data.summary.totalMigrated} utilisateur(s) migré(s)\n${response.data.summary.totalConflicts} conflit(s) résolu(s)`);

      // Réinitialiser les sélections
      setSelectedUsersForMigration([]);
      setMigrationPreview(null);
      setTargetOrganizationForMigration('');

      // Recharger les données
      await loadSuperAdminData();

    } catch (error) {
      console.error('Erreur lors de la migration:', error);
      alert('❌ Erreur lors de la migration: ' + (error.response?.data?.error || error.message));
    } finally {
      setExecutingMigration(false);
    }
  };

  const handleExecuteUserReset = async () => {
    if (!resetUserId) {
      alert('Veuillez sélectionner un utilisateur à réinitialiser');
      return;
    }

    const user = allUsers.find(u => u._id === resetUserId);
    const confirmation = window.confirm(
      `⚠️ ATTENTION: Réinitialisation complète des données\n\n` +
      `Utilisateur: ${user?.firstName} ${user?.lastName} (${user?.email})\n\n` +
      `Données à supprimer:\n` +
      `${resetOptions.nodes ? '• Tous les nœuds\n' : ''}` +
      `${resetOptions.relations ? '• Toutes les relations\n' : ''}` +
      `${resetOptions.tests ? '• Tous les tests\n' : ''}` +
      `${resetOptions.analytics ? '• Toutes les analytics\n' : ''}\n` +
      `Un backup complet sera créé automatiquement.\n\n` +
      `Cette action est IRRÉVERSIBLE. Continuer ?`
    );

    if (!confirmation) return;

    setExecutingReset(true);
    try {
      const response = await apiClient.post('/api/super-admin/reset-user-data', {
        userId: resetUserId,
        resetOptions,
        backupLabel: `Reset complet utilisateur ${user?.firstName} ${user?.lastName} - ${new Date().toISOString()}`
      });

      alert(`✅ Réinitialisation réussie!\n\nBackup créé: ${response.data.backupId}\nDonnées supprimées: ${Object.keys(resetOptions).filter(k => resetOptions[k]).join(', ')}`);

      // Réinitialiser les sélections
      setResetUserId('');
      setResetOptions({
        nodes: true,
        relations: true,
        tests: true,
        analytics: false
      });

      // Recharger les données
      await loadSuperAdminData();

    } catch (error) {
      console.error('Erreur lors du reset:', error);
      alert('❌ Erreur lors du reset: ' + (error.response?.data?.error || error.message));
    } finally {
      setExecutingReset(false);
    }
  };

  // ===== RÉINITIALISATION MOT DE PASSE (GÉNÉRATION) =====
  const handleResetPassword = async () => {
    if (!selectedUserForPwdReset) {
      alert('Veuillez sélectionner un utilisateur pour réinitialiser le mot de passe');
      return;
    }

    const user = allUsers.find(u => u._id === selectedUserForPwdReset);
    const confirmation = window.confirm(
      `⚠️ Réinitialiser le mot de passe pour ${user?.firstName} ${user?.lastName} (${user?.email}) ?`);

    if (!confirmation) return;

    setPasswordResetting(true);
    setGeneratedPassword('');
    setPasswordResetAlert(null);
    try {
  const resp = await apiClient.post(`/api/super-admin/users/${selectedUserForPwdReset}/reset-password`, {});
  // Accept multiple possible field names returned by the API
  const pwd = resp.data?.newPassword || resp.data?.password || resp.data?.generatedPassword || '';
      setGeneratedPassword(pwd);
      setPasswordResetAlert({ type: 'success', message: 'Mot de passe généré avec succès' });
    } catch (err) {
      console.error('Erreur reset password:', err);
      setPasswordResetAlert({ type: 'error', message: err.response?.data?.error || err.message || 'Erreur' });
    } finally {
      setPasswordResetting(false);
    }
  };

  const copyGeneratedPassword = async () => {
    if (!generatedPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPassword);
      alert('Mot de passe copié dans le presse-papiers');
    } catch (e) {
      // fallback
      const input = document.createElement('input');
      input.value = generatedPassword;
      document.body.appendChild(input);
      input.select();
      try { document.execCommand('copy'); alert('Mot de passe copié dans le presse-papiers'); } catch { alert('Impossible de copier automatiquement. Copiez manuellement.'); }
      document.body.removeChild(input);
    }
  };

  // Tester la connexion avec le mot de passe généré (ne stocke pas de cookie)
  const handleTestLogin = async () => {
    if (!generatedPassword || !selectedUserForPwdReset) return;
    const user = allUsers.find(u => u._id === selectedUserForPwdReset);
    if (!user) return alert('Utilisateur introuvable');

    try {
      const resp = await axios.post(`${API_BASE_URL}/auth/login`, {
        email: user.email,
        password: generatedPassword.trim()
      }, { withCredentials: false });

      if (resp?.data?.user) {
        alert('Test de connexion réussi pour ' + user.email + ' (le cookie n\'a pas été stocké dans le navigateur)');
      } else {
        alert('Test de connexion : réponse reçue mais utilisateur non retourné');
      }
    } catch (err) {
      console.error('Erreur test login:', err.response || err.message || err);
      const msg = err.response?.data?.error || err.message || 'Erreur lors du test de connexion';
      alert('Test de connexion échoué: ' + msg);
    }
  };

  if (loading) {
    return (
      <div className="super-admin-dashboard">
        <div className="loading-container">
          <div className="loading-animation">
            <div className="loading-spinner"></div>
          </div>
          <div className="loading-text">Chargement du tableau de bord super admin...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-dashboard">
      {/* Header avec gradient */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <div className="welcome-section">
              <h1>🔧 Super Administration</h1>
              <p>Tableau de bord principal - {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>

          <div className="header-right">
            {/* Indicateur de mode support tenant */}
            {selectedTenant && (
              <div className="impersonation-indicator">
                <span className="indicator-icon">👤</span>
                <div className="indicator-text">
                  <strong>Mode Support</strong>
                  <span>{selectedTenant.name}</span>
                </div>
                <button
                  onClick={() => handleTenantSelected(null)}
                  className="exit-btn"
                >
                  ❌
                </button>
              </div>
            )}

            {/* Notifications */}
            <div className="notifications-dropdown">
              <button className="notification-btn">
                🔔
                <span className="notification-badge">{notifications.length}</span>
              </button>
              <div className="notifications-panel">
                {notifications.map(notif => (
                  <div key={notif.id} className={`notification-item ${notif.type}`}>
                    <div className="notification-content">
                      <span className="notification-message">{notif.message}</span>
                      <span className="notification-time">{notif.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* User menu */}
            <div className="user-menu">
              <div className="user-avatar">
                <img src={`https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=667eea&color=fff`} alt="Avatar" />
              </div>
              <div className="user-info">
                <span className="user-name">{user.firstName} {user.lastName}</span>
                <span className="user-role">Super Administrateur</span>
              </div>
              <button
                onClick={handleLogout}
                className="logout-btn"
              >
                🚪
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation avec animations */}
      <nav className="dashboard-nav">
        <div className="nav-container">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {activeTab === tab.id && <div className="tab-indicator"></div>}
            </button>
          ))}
        </div>
      </nav>

      {/* Contenu principal */}
      <main className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            {/* Stats principales avec animations */}
            <div className="stats-grid">
              <div className="stat-card primary">
                <div className="stat-icon">🏢</div>
                <div className="stat-content">
                  <h3>Organisations</h3>
                  <div className="stat-number">{stats.organizationsCount}</div>
                  <div className="stat-trend positive">+3 ce mois</div>
                </div>
                <div className="stat-sparkline"></div>
              </div>

              <div className="stat-card success">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <h3>Utilisateurs</h3>
                  <div className="stat-number">{stats.usersCount}</div>
                  <div className="stat-trend positive">+12% vs hier</div>
                </div>
                <div className="stat-sparkline"></div>
              </div>

              <div className="stat-card warning">
                <div className="stat-icon">🕸️</div>
                <div className="stat-content">
                  <h3>Graphes</h3>
                  <div className="stat-number">{stats.graphsCount}</div>
                  <div className="stat-trend neutral">Stable</div>
                </div>
                <div className="stat-sparkline"></div>
              </div>

              <div className="stat-card info">
                <div className="stat-icon">🔐</div>
                <div className="stat-content">
                  <h3>Connexions Aujourd'hui</h3>
                  <div className="stat-number">{stats.todayLogins}</div>
                  <div className="stat-trend positive">+8% vs hier</div>
                </div>
                <div className="stat-sparkline"></div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="charts-section">
              <div className="chart-card">
                <h3>📊 Activité récente</h3>
                <div className="activity-timeline">
                  {stats.recentActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-time">{activity.time}</div>
                      <div className="activity-icon">{activity.icon}</div>
                      <div className="activity-dot"></div>
                      <div className="activity-description">{activity.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3>⚡ Performance système</h3>
                <div className="performance-metrics">
                  <div className="metric">
                    <span className="metric-label">Temps de réponse</span>
                    <div className="metric-bar">
                      <div className="metric-fill" style={{ width: '85%' }}></div>
                    </div>
                    <span className="metric-value">{stats.avgResponseTime}ms</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Requêtes/h</span>
                    <div className="metric-bar">
                      <div className="metric-fill success" style={{ width: '92%' }}></div>
                    </div>
                    <span className="metric-value">{stats.requestsPerHour}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Tokens actifs</span>
                    <div className="metric-bar">
                      <div className="metric-fill info" style={{ width: '78%' }}></div>
                    </div>
                    <span className="metric-value">{stats.activeTokens}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions rapides */}
            <div className="quick-actions">
              <h3>🚀 Actions rapides</h3>
              <div className="actions-grid">
                <button className="action-card" onClick={() => setActiveTab('organizations')}>
                  <div className="action-icon">🏢</div>
                  <div className="action-content">
                    <h4>Nouvelle organisation</h4>
                    <p>{t('organizations.createOrgAccount')}</p>
                  </div>
                </button>
                <button className="action-card" onClick={() => setActiveTab('users')}>
                  <div className="action-icon">👤</div>
                  <div className="action-content">
                    <h4>Nouvel utilisateur</h4>
                    <p>Ajouter un compte manuel</p>
                  </div>
                </button>
                <button className="action-card" onClick={() => setActiveTab('support-tenants')}>
                  <div className="action-icon">🛠️</div>
                  <div className="action-content">
                    <h4>Support tenant</h4>
                    <p>Accéder au mode support</p>
                  </div>
                </button>
                <button className="action-card" onClick={() => setActiveTab('security')}>
                  <div className="action-icon">🛡️</div>
                  <div className="action-content">
                    <h4>Logs sécurité</h4>
                    <p>Vérifier les alertes</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'licenses' && (
          <LicenseAdminDashboard user={user} />
        )}

        {activeTab === 'financial' && (
          <FinancialAdminDashboard user={user} />
        )}

        {activeTab === 'nodes' && (
          <div className="nodes-section">
            <SuperAdminNodesViewer user={user} />
          </div>
        )}

        {activeTab === 'organizations' && (
          <div className="organizations-section">
            <div className="section-header">
              <h2>🏢 Gestion des organisations</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  const createOrgSection = document.getElementById('create-org-form');
                  if (createOrgSection) {
                    createOrgSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                ➕ {t('organizations.createOrganization')}
              </button>
            </div>

            {/* Liste des organisations existantes */}
            <div className="organizations-grid">
              {organizations.map(org => (
                <div key={org.tenantId} className="org-card">
                  <div className="org-header">
                    <div className="org-avatar">
                      {org.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="org-info">
                      <h3>{org.name}</h3>
                      <p className="org-id">ID: {org.tenantId}</p>
                    </div>
                    <div className="org-status active">Actif</div>
                  </div>
                  <div className="org-stats">
                    <div className="org-stat">
                      <span className="stat-icon">👥</span>
                      <span>{org.usersCount} utilisateurs</span>
                    </div>
                    <div className="org-stat">
                      <span className="stat-icon">🕸️</span>
                      <span>{org.graphsCount} graphes</span>
                    </div>
                    <div className="org-stat">
                      <span className="stat-icon">📅</span>
                      <span>Créée le {org.createdAt ? new Date(org.createdAt).toLocaleDateString() : 'Inconnue'}</span>
                    </div>
                  </div>
                  <div className="org-actions">
                    <button className="btn-secondary">👁️ Voir</button>
                    <button className="btn-outline">✏️ Modifier</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Formulaire de création d'organisation */}
            <div id="create-org-form" className="create-organization-form">
              <h3>➕ {t('organizations.newOrganization')}</h3>
              <form onSubmit={handleCreateOrganization} className="organization-form">
                <div className="form-section">
                  <h4>📋 Informations Organisation</h4>
                  <div className="form-group">
                    <label htmlFor="name">Nom de l'organisation *</label>
                    <input
                      type="text"
                      id="name"
                      value={organizationForm.name}
                      onChange={(e) => setOrganizationForm({ ...organizationForm, name: e.target.value })}
                      required
                      placeholder={t('placeholders.companyName')}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      value={organizationForm.description}
                      onChange={(e) => setOrganizationForm({ ...organizationForm, description: e.target.value })}
                      placeholder={t('placeholders.companyDescription')}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="website">Site web</label>
                      <input
                        type="url"
                        id="website"
                        value={organizationForm.website}
                        onChange={(e) => setOrganizationForm({ ...organizationForm, website: e.target.value })}
                        placeholder={t('placeholders.companyUrl')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="plan">Plan *</label>
                      <select
                        id="plan"
                        value={organizationForm.plan}
                        onChange={(e) => setOrganizationForm({ ...organizationForm, plan: e.target.value })}
                      >
                        <option value="free">Free (1 users, 100 nodes)</option>
                        <option value="starter">Starter (10 users, 1k nodes)</option>
                        <option value="professional">Professional (50 users, 5k nodes)</option>
                        <option value="enterprise">Enterprise (100 users, 10k nodes)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>👤 Administrateur Principal</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="adminFirstName">Prénom *</label>
                      <input
                        type="text"
                        id="adminFirstName"
                        value={organizationForm.adminFirstName}
                        onChange={(e) => setOrganizationForm({ ...organizationForm, adminFirstName: e.target.value })}
                        required
                        placeholder={t('placeholders.firstName')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="adminLastName">Nom *</label>
                      <input
                        type="text"
                        id="adminLastName"
                        value={organizationForm.adminLastName}
                        onChange={(e) => setOrganizationForm({ ...organizationForm, adminLastName: e.target.value })}
                        required
                        placeholder={t('placeholders.lastName')}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="adminEmail">Email *</label>
                    <input
                      type="email"
                      id="adminEmail"
                      value={organizationForm.adminEmail}
                      onChange={(e) => setOrganizationForm({ ...organizationForm, adminEmail: e.target.value })}
                      required
                      placeholder={t('placeholders.adminEmail')}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="adminPassword">Mot de passe *</label>
                    <input
                      type="password"
                      id="adminPassword"
                      value={organizationForm.adminPassword}
                      onChange={(e) => setOrganizationForm({ ...organizationForm, adminPassword: e.target.value })}
                      required
                      placeholder={t('placeholders.securePassword')}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={creating}>
                    {creating ? '⏳ ' + t('organizations.creating') : '✅ ' + t('organizations.createOrganization')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h2>👥 Gestion des utilisateurs</h2>
              <div className="search-filters">
                <input type="text" placeholder={t('placeholders.searchUser')} className="search-input" />
                <select className="filter-select">
                  <option value="">Tous les rôles</option>
                  <option value="super-admin">Super Admin</option>
                  <option value="org-admin">Admin Organisation</option>
                  <option value="user">Utilisateur</option>
                </select>
              </div>
            </div>

            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Organisation</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Dernière connexion</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map(userData => (
                    <tr key={userData._id}>
                      <td>
                        <div className="user-cell">
                          <img src={`https://ui-avatars.com/api/?name=${userData.firstName}+${userData.lastName}&size=32`} alt="Avatar" />
                          <div>
                            <div className="user-name">{userData.firstName} {userData.lastName}</div>
                            <div className="user-email">{userData.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{userData.organizationName}</td>
                      <td>
                        <span className={`role-badge ${userData.role}`}>
                          {userData.role}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${userData.status}`}>
                          {userData.status}
                        </span>
                      </td>
                      <td>{userData.lastLoginAt ? new Date(userData.lastLoginAt).toLocaleDateString() : 'Jamais'}</td>
                      <td>
                        <div className="table-actions">
                          <button className="btn-icon" onClick={() => handleViewUser(userData)}>👁️</button>
                          <button className="btn-icon" onClick={() => handleEditUser(userData)}>✏️</button>
                          <button className="btn-icon danger" onClick={() => handleDeleteUser(userData)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section Gestion des Associations Utilisateur-Organisation */}
            <div className="user-organization-management">
              <h3>🔗 Gestion des Associations Utilisateur-Organisation</h3>

              {/* Associer un utilisateur à une organisation */}
              <div className="association-section">
                <h4>➕ Associer un utilisateur à une organisation</h4>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const email = e.target.email.value;
                    const tenantId = e.target.tenantId.value;
                    const role = e.target.role.value;
                    handleApplyInvitationCode(email, tenantId, role);
                  }}
                  className="association-form"
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label>Utilisateur :</label>
                      <select name="email" required>
                        <option value="">Sélectionnez un utilisateur</option>
                        {users.length > 0 ? (
                          users.map((user) => (
                            <option key={user.email} value={user.email}>
                              {user.email} ({user.firstName} {user.lastName})
                            </option>
                          ))
                        ) : (
                          <option disabled>Aucun utilisateur disponible</option>
                        )}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Organisation :</label>
                      <select name="tenantId" required>
                        <option value="">Sélectionnez une organisation</option>
                        {organizations.length > 0 ? (
                          organizations.map((org) => (
                            <option key={org.tenantId} value={org.tenantId}>
                              {org.name} (Tenant ID: {org.tenantId})
                            </option>
                          ))
                        ) : (
                          <option disabled>Aucune organisation disponible</option>
                        )}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Rôle :</label>
                      <select name="role">
                        <option value="user">Utilisateur</option>
                        <option value="org-admin">Admin d'organisation</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="btn-primary">Associer l'utilisateur</button>
                </form>
              </div>

              {/* Retirer un utilisateur d'une organisation */}
              <div className="removal-section">
                <h4>➖ Retirer un utilisateur d'une organisation</h4>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const email = e.target.email.value;
                    handleRemoveUserFromOrganization(email);
                  }}
                  className="removal-form"
                >
                  <div className="form-group">
                    <label>Utilisateur à retirer :</label>
                    <select name="email" required>
                      <option value="">Sélectionnez un utilisateur</option>
                      {allUsers.filter(user => user.organizationName).map((user) => (
                        <option key={user.email} value={user.email}>
                          {user.email} ({user.firstName} {user.lastName}) - {user.organizationName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" className="btn-secondary">Retirer l'utilisateur</button>
                </form>
              </div>

              {/* Générer un code d'invitation */}
              <div className="invitation-section">
                <h4>🎫 Générer un code d'invitation</h4>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const email = e.target.email.value;
                    const tenantId = e.target.tenantId.value;
                    const role = e.target.role.value;
                    handleGenerateInvitationCode(email, tenantId, role);
                  }}
                  className="invitation-form"
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email utilisateur :</label>
                      <input
                        type="email"
                        name="email"
                        placeholder={t('placeholders.userEmail')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Organisation :</label>
                      <select name="tenantId" required>
                        <option value="">Sélectionnez une organisation</option>
                        {organizations.map((org) => (
                          <option key={org.tenantId} value={org.tenantId}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Rôle :</label>
                      <select name="role">
                        <option value="user">Utilisateur</option>
                        <option value="org-admin">Admin d'organisation</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="btn-primary">Générer le code</button>
                </form>
              </div>
            </div>

            {/* Section migration des utilisateurs orphelins */}
            <div className="migration-container">
              <h3>🔄 Migration des Utilisateurs Orphelins</h3>
              <p className="migration-description">
                Ces utilisateurs sont associés au "default-tenant" et doivent être migrés vers une organisation réelle.
              </p>

              {orphanUsers.length > 0 ? (
                <div className="migration-section">
                  <h4>👥 Utilisateurs à migrer ({orphanUsers.length})</h4>
                  <div className="orphan-users-list">
                    {orphanUsers.map((user, index) => (
                      <div key={index} className="orphan-user-item">
                        <div className="user-info">
                          <strong>{user.firstName} {user.lastName}</strong>
                          <span className="user-email">{user.email}</span>
                          <span className={`user-role ${user.role}`}>{user.role}</span>
                        </div>
                        <div className="user-meta">
                          <span className={`user-status ${user.status}`}>{user.status}</span>
                          <span className="user-date">
                            Créé le {new Date(user.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="migration-form">
                    <h4>🎯 Sélectionner l'organisation cible</h4>
                    <div className="form-group">
                      <label htmlFor="migrationTarget">Organisation de destination :</label>
                      <select
                        id="migrationTarget"
                        value={migrationTarget}
                        onChange={(e) => setMigrationTarget(e.target.value)}
                        required
                      >
                        <option value="">-- Sélectionner une organisation --</option>
                        {organizations.map((org) => (
                          <option key={org.tenantId} value={org.tenantId}>
                            {org.name} (Plan: {org.plan})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="migration-actions">
                      <button
                        onClick={handleMigrateUsers}
                        disabled={!migrationTarget || migrating}
                        className="btn-primary"
                      >
                        {migrating ? '⏳ Migration en cours...' : '🚀 Migrer tous les utilisateurs'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-orphan-users">
                  <p>✅ Aucun utilisateur orphelin trouvé. Tous les utilisateurs sont correctement associés.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'migrations' && (
          <div className="migrations-section">
            <div className="section-header">
              <h2>🔄 Gestion des Migrations Utilisateur</h2>
              <p className="section-description">
                Outils sécurisés pour migrer des utilisateurs vers des organisations ou réinitialiser leurs données
              </p>
            </div>

            {/* Section Migration vers Organisation */}
            <div className="migration-card">
              <h3>🏢 Migration vers Organisation</h3>
              <p>Migrer des utilisateurs individuels vers une organisation existante</p>

              <div className="migration-form">
                {/* Sélection des utilisateurs à migrer */}
                <div className="form-group">
                  <label>
                    👥 Utilisateurs sans organisation à migrer ({allUsers.filter(user =>
                      (!user.organizationId || user.organizationId === 'default-tenant') &&
                      user.role !== 'super-admin' &&
                      !user.isSuperAdmin
                    ).length} disponibles) :
                  </label>
                  <div className="users-selection">
                    {allUsers
                      .filter(user =>
                        // Filtrer uniquement les utilisateurs sans organisation
                        (!user.organizationId || user.organizationId === 'default-tenant') &&
                        // Exclure les super admins
                        user.role !== 'super-admin' &&
                        !user.isSuperAdmin
                      )
                      .map(user => (
                        <label key={user._id} className="user-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedUsersForMigration.includes(user._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsersForMigration([...selectedUsersForMigration, user._id]);
                              } else {
                                setSelectedUsersForMigration(selectedUsersForMigration.filter(id => id !== user._id));
                              }
                            }}
                          />
                          <span className="user-info">
                            <strong>{user.firstName} {user.lastName}</strong>
                            <span className="user-email">{user.email}</span>
                            <span className="user-role-badge">{user.role}</span>
                            <span className="org-status">Sans organisation</span>
                          </span>
                        </label>
                      ))
                    }
                    {allUsers.filter(user =>
                      (!user.organizationId || user.organizationId === 'default-tenant') &&
                      user.role !== 'super-admin' &&
                      !user.isSuperAdmin
                    ).length === 0 && (
                        <div className="no-users-message">
                          <span className="info-icon">ℹ️</span>
                          <span>Aucun utilisateur sans organisation disponible pour migration</span>
                        </div>
                      )}
                  </div>
                </div>

                {/* Sélection organisation cible */}
                <div className="form-group">
                  <label>🏢 Organisation cible :</label>
                  <select
                    value={targetOrganizationForMigration}
                    onChange={(e) => setTargetOrganizationForMigration(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Sélectionner une organisation</option>
                    {organizations.map(org => (
                      <option key={org.tenantId} value={org.tenantId}>
                        {org.name} ({org.tenantId})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Stratégie de migration */}
                <div className="form-group">
                  <label>🔄 Stratégie de migration :</label>
                  <select
                    value={migrationStrategy}
                    onChange={(e) => setMigrationStrategy(e.target.value)}
                    className="form-select"
                  >
                    <option value="merge">Fusionner les données (recommandé)</option>
                    <option value="replace">Remplacer par les données org</option>
                    <option value="archive">Archiver les données utilisateur</option>
                  </select>
                </div>

                {/* Boutons d'action */}
                <div className="migration-actions">
                  <button
                    onClick={handleGenerateMigrationPreview}
                    disabled={loadingMigrationPreview || selectedUsersForMigration.length === 0 || !targetOrganizationForMigration}
                    className="btn-secondary"
                  >
                    {loadingMigrationPreview ? '⏳ Analyse...' : '🔍 Aperçu Migration'}
                  </button>

                  {migrationPreview && (
                    <button
                      onClick={handleExecuteMigration}
                      disabled={executingMigration}
                      className="btn-primary"
                    >
                      {executingMigration ? '⏳ Migration...' : '🚀 Exécuter Migration'}
                    </button>
                  )}
                </div>

                {/* Aperçu de migration */}
                {migrationPreview && (
                  <div className="migration-preview">
                    <h4>📊 Aperçu de la Migration</h4>
                    <div className="preview-stats">
                      <div className="stat-item">
                        <span className="stat-label">Utilisateurs :</span>
                        <span className="stat-value">{migrationPreview.summary.totalUsers}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Conflits détectés :</span>
                        <span className="stat-value">{migrationPreview.summary.totalConflicts}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Durée estimée :</span>
                        <span className="stat-value">{migrationPreview.summary.estimatedDuration}s</span>
                      </div>
                    </div>

                    <div className="preview-details">
                      {migrationPreview.users.map(userData => (
                        <div key={userData.userId} className="user-preview">
                          <h5>👤 {allUsers.find(u => u._id === userData.userId)?.firstName} {allUsers.find(u => u._id === userData.userId)?.lastName}</h5>
                          <div className="data-summary">
                            <span>📦 {userData.userData.nodes?.length || 0} nœuds</span>
                            <span>🔗 {userData.userData.relations?.length || 0} relations</span>
                            <span>🧪 {userData.userData.tests?.length || 0} tests</span>
                          </div>
                          {userData.conflicts.length > 0 && (
                            <div className="conflicts">
                              <span className="conflicts-count">⚠️ {userData.conflicts.length} conflit(s)</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section Reset Utilisateur Freelance */}
            <div className="reset-card">
              <h3>🗑️ Reset Utilisateur Freelance</h3>
              <p>Réinitialisation complète des données d'un utilisateur (backup automatique)</p>

              <div className="reset-form">
                <div className="form-group">
                  <label>👤 Utilisateur à réinitialiser :</label>
                  <select
                    value={resetUserId}
                    onChange={(e) => setResetUserId(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Sélectionner un utilisateur</option>
                    {allUsers
                      .filter(user =>
                        // Exclure les super admins pour la sécurité
                        user.role !== 'super-admin' &&
                        !user.isSuperAdmin
                      )
                      .map(user => (
                        <option key={user._id} value={user._id}>
                          {user.firstName} {user.lastName} ({user.email}) - Org: {user.organizationName || 'Aucune'} - Rôle: {user.role}
                        </option>
                      ))
                    }
                  </select>
                  {allUsers.filter(user => user.role !== 'super-admin' && !user.isSuperAdmin).length === 0 && (
                    <div className="no-users-message">
                      <span className="warning-icon">⚠️</span>
                      <span>Aucun utilisateur non-admin disponible pour reset</span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>🗂️ Données à réinitialiser :</label>
                  <div className="reset-options">
                    <label className="option-checkbox">
                      <input
                        type="checkbox"
                        checked={resetOptions.nodes}
                        onChange={(e) => setResetOptions({ ...resetOptions, nodes: e.target.checked })}
                      />
                      <span>📦 Nœuds</span>
                    </label>
                    <label className="option-checkbox">
                      <input
                        type="checkbox"
                        checked={resetOptions.relations}
                        onChange={(e) => setResetOptions({ ...resetOptions, relations: e.target.checked })}
                      />
                      <span>🔗 Relations</span>
                    </label>
                    <label className="option-checkbox">
                      <input
                        type="checkbox"
                        checked={resetOptions.tests}
                        onChange={(e) => setResetOptions({ ...resetOptions, tests: e.target.checked })}
                      />
                      <span>🧪 Tests</span>
                    </label>
                    <label className="option-checkbox">
                      <input
                        type="checkbox"
                        checked={resetOptions.analytics}
                        onChange={(e) => setResetOptions({ ...resetOptions, analytics: e.target.checked })}
                      />
                      <span>📈 Analytics</span>
                    </label>
                  </div>
                </div>

                <div className="reset-actions">
                  <button
                    onClick={handleExecuteUserReset}
                    disabled={!resetUserId || executingReset}
                    className="btn-danger"
                  >
                    {executingReset ? '⏳ Reset...' : '🗑️ Réinitialiser Utilisateur'}
                  </button>
                </div>
              </div>
            </div>

            {/* Section Réinitialiser le mot de passe */}
            <div className="password-reset-card" style={{ marginTop: '1rem', border: '1px solid #f0f0f0', padding: '1rem', borderRadius: '6px' }}>
              <h3>🔑 Réinitialiser mot de passe</h3>
              <p>Générer un nouveau mot de passe pour un utilisateur (ne concerne pas les super-admins)</p>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>👤 Utilisateur :</label>
                  <select
                    value={selectedUserForPwdReset}
                    onChange={(e) => setSelectedUserForPwdReset(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Sélectionner un utilisateur</option>
                    {allUsers
                      .filter(u => u.role !== 'super-admin' && !u.isSuperAdmin)
                      .map(u => (
                        <option key={u._id} value={u._id}>{u.firstName} {u.lastName} ({u.email})</option>
                      ))}
                  </select>
                </div>

                <div className="form-group" style={{ alignSelf: 'flex-end', marginLeft: '1rem' }}>
                  <button
                    onClick={handleResetPassword}
                    disabled={!selectedUserForPwdReset || passwordResetting}
                    className="btn-primary"
                  >
                    {passwordResetting ? '⏳ Réinitialisation...' : '🔑 Réinitialiser mot de passe'}
                  </button>
                </div>
              </div>

              {passwordResetAlert && (
                <div className={`alert ${passwordResetAlert.type === 'error' ? 'alert-danger' : 'alert-success'}`} style={{ marginTop: '0.75rem' }}>
                  {passwordResetAlert.message}
                </div>
              )}

              {generatedPassword && (
                <div className="generated-password" style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input readOnly value={generatedPassword} style={{ flex: 1 }} />
                  <button onClick={copyGeneratedPassword} className="btn-secondary">Copier</button>
                  <button onClick={handleTestLogin} className="btn-outline">Tester connexion</button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="requests-section">
            <EntityRequestManager user={user} />
          </div>
        )}

        {activeTab === 'support-tenants' && (
          <div className="support-tenants-section">
            {selectedTenant ? (
              <div className="tenant-view-container">
                {/* Header de contexte */}
                <div className="tenant-context-header">
                  <div className="context-info">
                    <span className="context-icon">🏢</span>
                    <div className="context-text">
                      <h2>Graphe de {selectedTenant.name}</h2>
                      <p>Mode support - Visualisation tenant: {selectedTenant.tenantId}</p>
                    </div>
                  </div>

                  <div className="context-actions">
                    <button
                      onClick={() => handleTenantSelected(null)}
                      className="context-btn secondary"
                    >
                      ⬅️ Retour sélection
                    </button>
                    <button
                      onClick={() => handleTenantSelected(null)}
                      className="context-btn primary"
                    >
                      🏠 Retour admin
                    </button>
                  </div>
                </div>

                {/* Composant principal avec contexte tenant */}
                <div className="tenant-content">
                  <FeatureManager
                    isSuperAdminMode={true}
                    tenantContext={selectedTenant}
                    onExitSupport={() => handleTenantSelected(null)}
                  />
                </div>
              </div>
            ) : (
              <SuperAdminTenantSelector
                onTenantSelected={handleTenantSelected}
                selectedTenantId={selectedTenant?.tenantId}
              />
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-section">
            <h2>📈 Analytics d'Usage Globales</h2>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              Visualisez l'usage de toutes les organisations et utilisateurs
            </p>
            {/* UsageDashboard expects license/usage payload; keep default behavior */}
            <UsageDashboard user={user} />

            {/* Impact analytics (from backend /impact-analytics) */}
            <div style={{ marginTop: '2rem' }}>
              <ImpactAnalyzer />
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="system-section">
            <h2>🔧 Administration système</h2>
            <p>Outils d'administration système à venir...</p>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="security-section">
            <h2>🛡️ Journaux de sécurité</h2>
            <SuperAdminPromptInjectionLogs user={user} />
          </div>
        )}

        {/* Modals */}
        {isViewModalOpen && selectedUser && (
          <div className="modal-overlay" onClick={() => setIsViewModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Détails de l'utilisateur</h3>
              <p><strong>Nom:</strong> {selectedUser.firstName} {selectedUser.lastName}</p>
              <p><strong>Email:</strong> {selectedUser.email}</p>
              <p><strong>Rôle:</strong> {selectedUser.role}</p>
              <p><strong>Organisation:</strong> {selectedUser.organizationName}</p>
              <p><strong>Statut:</strong> {selectedUser.status}</p>
              <p><strong>Dernière connexion:</strong> {selectedUser.lastLoginAt ? new Date(selectedUser.lastLoginAt).toLocaleDateString() : 'Jamais'}</p>
              <button onClick={() => setIsViewModalOpen(false)}>{t('common.close')}</button>
            </div>
          </div>
        )}

        {isEditModalOpen && userToEdit && (
          <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Modifier l'utilisateur</h3>
              <label>
                Prénom :
                <input
                  type="text"
                  value={userToEdit.firstName || ''}
                  onChange={(e) => setUserToEdit({ ...userToEdit, firstName: e.target.value })}
                />
              </label>
              <label>
                Nom :
                <input
                  type="text"
                  value={userToEdit.lastName || ''}
                  onChange={(e) => setUserToEdit({ ...userToEdit, lastName: e.target.value })}
                />
              </label>
              <label>
                Email :
                <input
                  type="email"
                  value={userToEdit.email || ''}
                  onChange={(e) => setUserToEdit({ ...userToEdit, email: e.target.value })}
                />
              </label>
              <label>
                Rôle :
                <select
                  value={userToEdit.role}
                  onChange={(e) => setUserToEdit({ ...userToEdit, role: e.target.value })}
                >
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                  <option value="super-admin">Super Administrateur</option>
                </select>
              </label>
              <label>
                Statut :
                <select
                  value={userToEdit.status}
                  onChange={(e) => setUserToEdit({ ...userToEdit, status: e.target.value })}
                >
                  <option value="active">Actif</option>
                  <option value="suspended">Suspendu</option>
                </select>
              </label>
              <div className="modal-actions">
                <button onClick={saveUserDetails}>{t('actions.save')}</button>
                <button onClick={() => setIsEditModalOpen(false)}>{t('actions.cancel')}</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
